import type { ContainerInput } from '@chukua/common';
import { Button, Popconfirm, Typography } from 'antd';
import { Container, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import type { ContainerSize, ContainerType } from '../../../../reference-data/reference-data.api';
import { ContainerModal } from './ContainerModal';
import type { JobContainer } from '../../../jobs.api';

interface ContainerListProps {
  containers: JobContainer[];
  containerSizes: ContainerSize[];
  containerTypes: ContainerType[];
  saving: boolean;
  onAdd: (input: ContainerInput) => Promise<boolean>;
  onUpdate: (containerId: string, input: ContainerInput) => Promise<boolean>;
  onRemove: (containerId: string, number: string) => Promise<boolean>;
}

function sizeName(containerSizes: ContainerSize[], code: string): string {
  return containerSizes.find((s) => s.code === code)?.name ?? code;
}

function typeName(containerTypes: ContainerType[], code: string): string {
  return containerTypes.find((t) => t.code === code)?.name ?? code;
}

/** The containers on a container job (PRD → Job intake → Captured per job type (Container); gitaumoses4/chukua#31) — add/edit/remove, same shape as CustomerContacts. */
export function ContainerList({ containers, containerSizes, containerTypes, saving, onAdd, onUpdate, onRemove }: ContainerListProps) {
  const [editing, setEditing] = useState<JobContainer | 'new' | null>(null);

  async function submit(input: ContainerInput) {
    if (editing === 'new') await onAdd(input);
    else if (editing) await onUpdate(editing.id, input);
    setEditing(null);
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-fg">Containers</h2>
        <Button size="small" icon={<Plus className="size-3.5" />} onClick={() => setEditing('new')}>
          Add container
        </Button>
      </div>
      {containers.length === 0 && <EmptyState icon={Container} title="No containers yet" description="Add the containers on this job." />}
      {containers.length > 0 && (
        <ul className="flex flex-col gap-3">
          {containers.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 rounded-lg border border-line bg-surface px-4 py-3">
              <div>
                <Typography.Text code>{c.number}</Typography.Text>
                <p className="text-sm text-muted">
                  {sizeName(containerSizes, c.sizeCode)} · {typeName(containerTypes, c.typeCode)}
                  {c.seal && ` · Seal ${c.seal}`}
                  {c.grossWeightKg !== null && ` · ${c.grossWeightKg.toLocaleString('en-KE')} kg`}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="text"
                  size="small"
                  icon={<Pencil className="size-3.5" />}
                  onClick={() => setEditing(c)}
                  aria-label={`Edit container ${c.number}`}
                />
                <Popconfirm title={`Remove container ${c.number}?`} onConfirm={() => onRemove(c.id, c.number)}>
                  <Button type="text" size="small" danger icon={<Trash2 className="size-3.5" />} aria-label={`Remove container ${c.number}`} />
                </Popconfirm>
              </div>
            </li>
          ))}
        </ul>
      )}
      <ContainerModal
        open={editing !== null}
        container={editing === 'new' ? null : editing}
        containerSizes={containerSizes}
        containerTypes={containerTypes}
        submitting={saving}
        onSubmit={async (input) => {
          await submit(input);
          return true;
        }}
        onCancel={() => setEditing(null)}
      />
    </section>
  );
}
